import React, { useEffect, useState, useContext } from "react"
import styled from "@emotion/styled"
import tw from "twin.macro"
import Container from "./Container"
import Button from "./Button"
import RichText from "./RichText"
import PropTypes from "prop-types"
import Cookies from "js-cookie"
import ThemeContext from "../context/theme-context"

const StyledCookieConsent = styled.div`
  ${tw`fixed bottom-0 left-0 w-full bg-black text-white leading-normal py-4 tracking-wide z-50`}
`

const Row = styled.div`
  ${tw`flex flex-wrap sm:flex-nowrap -ml-8 -mt-4 sm:justify-end`}
`

const Col = styled.div`
  ${tw`pl-8 mt-4`}
`

const TextCol = styled(Col)`
  ${tw`w-full sm:w-auto flex-grow`}
  a {
    ${tw`underline text-gray-light hover:no-underline`}
  }
`

const ButtonCol = styled(Col)`
  ${tw`w-auto flex-shrink-0`}
`

const CookieConsent = ({
  text,
  textLinks,
  buttonLabel,
  acceptOnScrollPercentage,
  onAccept,
}) => {
  const { hydrated: isClient } = useContext(ThemeContext)

  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isClient) return

    if (Cookies.get("ecmCookiesAccepted") === undefined) {
      setVisible(true)
      window.addEventListener("scroll", handleScroll)
      document.body.addEventListener("click", handleClickOutsideBanner)
    }

    // Cleanup function
    return () => {
      document.body.removeEventListener("click", handleClickOutsideBanner)
      window.removeEventListener("scroll", handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient])

  if (!isClient) {
    return null
  }

  const accept = () => {
    onAccept()

    // remove listener if set
    window.removeEventListener("scroll", handleScroll)
    document.body.removeEventListener("click", handleClickOutsideBanner)

    Cookies.set("ecmCookiesAccepted", true, { expires: 365 })

    setVisible(false)
  }

  const handleScroll = () => {
    // (top / height) - height * 100
    let rootNode = document.documentElement,
      body = document.body,
      top = "scrollTop",
      height = "scrollHeight"

    let percentage =
      ((rootNode[top] || body[top]) /
        ((rootNode[height] || body[height]) - rootNode.clientHeight)) *
      100

    if (percentage > acceptOnScrollPercentage) {
      accept()
    }
  }

  const handleClickOutsideBanner = (event) => {
    // Remove once invoked so it only fires once. IE11 doesn’t support `{ once: true }` option.
    document.body.removeEventListener("click", handleClickOutsideBanner)
    // Get cookie consent banner element so we can avoid accepting cookies if the user clicks
    // within the cookie banner itself.
    const bannerElement = document.querySelector(".js-cookie-consent")

    // The click handler is set to only trigger once, so we remove and re-add it if the user clicks
    // within the cookie banner so analytics will be tracked once they click outside the banner
    if (bannerElement && bannerElement.contains(event.target)) {
      document.body.addEventListener("click", handleClickOutsideBanner)
    } else {
      accept()
    }
  }

  if (!visible) {
    return null
  }

  return (
    <StyledCookieConsent className="js-cookie-consent">
      <Container>
        <Row>
          <TextCol>
            <RichText styled={false} links={textLinks}>
              {text}
            </RichText>
          </TextCol>
          <ButtonCol>
            <Button
              contrast={true}
              as="button"
              onClick={() => {
                accept()
              }}
            >
              {buttonLabel}
            </Button>
          </ButtonCol>
        </Row>
      </Container>
    </StyledCookieConsent>
  )
}

CookieConsent.propTypes = {
  text: PropTypes.string,
  buttonLabel: PropTypes.string,
  acceptOnScrollPercentage: PropTypes.number,
  onAccept: PropTypes.func,
}

CookieConsent.defaultProps = {
  acceptOnScrollPercentage: 25,
  onAccept: () => {},
}

export default CookieConsent
